import { AccessType } from '@modules/access/types';
import type { TItem, TResolvedItemsMap } from '@modules/items/types/items.types';
import { isTSearchItem } from '@modules/search/types';
import type { TTaskLinkedItem, TTaskLinkedItemType, TUpdateTaskDetails } from './types';

// Mongo ids are unique per collection, so a linked item is identified by its type and id.
export const getLinkedItemKey = ({ _id, type }: Pick<TItem, '_id' | 'type'>) => `${type}:${_id}`;

/** Linked items in link order. Items that no longer exist are missing from `resolvedItems` and skipped. */
export const resolveLinkedItems = (linkedItems: TTaskLinkedItem[], resolvedItems: TResolvedItemsMap): TItem[] =>
  linkedItems
    .filter(({ item, itemType }) => resolvedItems[item]?.item.type === itemType)
    .map(({ item }) => resolvedItems[item].item);

/** Why the user cannot open a linked item. Folders with partial access still open. */
export const getUnavailableLabel = (item: TItem): string | undefined =>
  item.stats.archivedDate ? 'Unavailable' : item.accessRight === AccessType.NONE ? 'No access' : undefined;

/** Search results carry a snippet of matching text, which the items table would show under the name. */
export const withoutSearchSnippet = (item: TItem): TItem => {
  if (!isTSearchItem(item)) return item;
  const { snippet, ...rest } = item;
  return rest;
};

// The picker selects only folders, documents and files.
export const toTaskLinkedItem = ({ _id, type }: TItem): TTaskLinkedItem => ({
  item: _id,
  itemType: type as TTaskLinkedItemType,
});

/** The links to add and remove, compared with the links the form started from. */
export const getLinkedItemChanges = (
  saved: TItem[],
  edited: TItem[]
): Pick<TUpdateTaskDetails, 'addedLinkedItems' | 'removedLinkedItems'> => {
  const savedKeys = new Set(saved.map(getLinkedItemKey));
  const editedKeys = new Set(edited.map(getLinkedItemKey));
  const added = edited.filter((item) => !savedKeys.has(getLinkedItemKey(item))).map(toTaskLinkedItem);
  const removed = saved.filter((item) => !editedKeys.has(getLinkedItemKey(item))).map(toTaskLinkedItem);
  return {
    ...(added.length ? { addedLinkedItems: added } : {}),
    ...(removed.length ? { removedLinkedItems: removed } : {}),
  };
};
